import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { Button } from "simplydo/react/ui/button";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { IconData } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Loader } from "simplydo/react/ui/loader";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import { useCurrentUser } from "utils/currentUser";

import ConfigurableTable from "../ConfigurableTable";
import { useTableSelection } from "../ConfigurableTable/useTableSelection";

const IMPORTABLE_FIELD_SECTION_TYPES = new Set(["custom", "edi"]);

type IdeaTemplate = OpenAPI.Schemas["IdeaTemplate"];
type IdeaTemplateBody = NonNullable<IdeaTemplate["body"]>;

const isImportableFieldSection = (section: IdeaTemplateBody[number]) =>
  !section.type || IMPORTABLE_FIELD_SECTION_TYPES.has(section.type);

const getFieldImportKey = (
  challengeId: string,
  sectionId: string,
  fieldId: string,
  fieldIndex: number,
) =>
  `${challengeId}:${sectionId || "unknown-section"}:${fieldId || `field-${fieldIndex}`}`;

const getFieldDisplayName = (
  field: IdeaTemplateBody[number]["fields"][number],
) => {
  if (field.type === "row") return "Row divider";
  if (field.type === "infotext") return "Information text";
  return field.title;
};

type TemplateFieldType = {
  key: string;
  name: string;
  type: string;
  icon?: IconData;
};

type ImportableField = {
  importKey: string;
  sectionName: string;
  field: IdeaTemplateBody[number]["fields"][number];
  challenge: OpenAPI.Schemas["Challenge"];
  challengeName: string;
  disabled: boolean;
};

type FieldChooserProps = {
  isOpen: boolean;
  onClose: () => void;
  addFieldsToEndOfSection: (
    fields: (IdeaTemplateBody[number]["fields"][number] & {
      challenge?: string;
    })[],
  ) => void;
  body: IdeaTemplateBody;
  challengeId: string;
  templateFieldTypes: TemplateFieldType[];
};

const FieldChooser = ({
  isOpen,
  onClose,
  addFieldsToEndOfSection,
  body,
  challengeId,
  templateFieldTypes,
}: FieldChooserProps) => {
  const [searchLoading, setSearchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importableFields, setImportableFields] = useState<ImportableField[]>(
    [],
  );
  const fieldSelection = useTableSelection<ImportableField>({
    getKey: (item) => item.importKey,
    selectAll: false,
  });
  const selectedFields = fieldSelection.keys;
  const [searchFieldType, setSearchFieldType] = useState("");
  const { t } = useTranslation();

  const user = useCurrentUser();
  const userId = user?._id;

  const toImportableFields = useCallback(
    (
      challenges: (OpenAPI.Schemas["Challenge"] & {
        template?: IdeaTemplate;
      })[] = [],
    ) => {
      // Collect field IDs already present in the current challenge.
      const currentChallengeFieldIds = new Set(
        body.reduce((ids, section) => {
          (section.fields || []).forEach((field) => {
            ids.push(field.id);
          });
          return ids;
        }, [] as string[]),
      );

      return challenges
        .filter((challenge) => challenge._id !== challengeId)
        .filter((challenge) => {
          const challengeBody = challenge.template?.body;
          return Array.isArray(challengeBody) && challengeBody.length > 0;
        })
        .reduce((allFields, challenge) => {
          const challengeBody = challenge.template?.body || [];

          const challengeFields = challengeBody
            .filter(isImportableFieldSection)
            .reduce((fields, section) => {
              const sectionFields = (section.fields || []).map(
                (field, fieldIndex) => ({
                  importKey: getFieldImportKey(
                    challenge._id,
                    section.id,
                    field.id,
                    fieldIndex,
                  ),
                  sectionName: section.name || "Untitled section",
                  field,
                  challenge,
                  challengeName: challenge.name,
                  disabled: currentChallengeFieldIds.has(field.id),
                }),
              );
              return [...fields, ...sectionFields];
            }, [] as ImportableField[]);

          return [...allFields, ...challengeFields];
        }, [] as ImportableField[]);
    },
    [body, challengeId],
  );

  const throttledFetch = useThrottle(api.users.getChallenges);
  const fetchImportableFields = useCallback(
    (query: string = "") => {
      if (!userId) return;

      setSearchLoading(true);
      throttledFetch(
        userId,
        { page: 1, limit: 10, query, forType: "ideaTemplate" },
        (data) => {
          setSearchLoading(false);
          setLoading(false);
          setImportableFields(toImportableFields(data.challenges || []));
        },
        () => {
          setLoading(false);
          setSearchLoading(false);
        },
      );
    },
    [throttledFetch, userId, toImportableFields],
  );

  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      setLoading(true);
      fetchImportableFields();
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen, fetchImportableFields]);

  const handleImport = () => {
    const fieldsToImport = importableFields
      .filter((item) => selectedFields.includes(item.importKey))
      .map((item) => ({ ...item.field, challenge: item.challenge._id }));
    api.challenges.copyFields(
      challengeId,
      { fields: fieldsToImport },
      ({ copiedFields }) => {
        addFieldsToEndOfSection(copiedFields);
      },
      () => {},
    );
    onClose();
    fieldSelection.clear();
  };

  const filteredFields = useMemo(() => {
    return importableFields.filter((item) => {
      const matchesType = searchFieldType
        ? item.field.type === searchFieldType
        : true;

      return matchesType;
    });
  }, [importableFields, searchFieldType]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalHeader>Import an existing field</ModalHeader>
      <ModalContent scrolling>
        {loading && importableFields.length === 0 ? (
          <Loader>Loading fields...</Loader>
        ) : (
          <>
            <div style={{ display: "flex", gap: "1px", marginBottom: "10px" }}>
              <div style={{ marginBottom: "5px", flex: 1, minWidth: "200px" }}>
                <Input
                  loading={searchLoading}
                  placeholder={`Field or ${t("generic.challenge")} name`}
                  onChange={(_e, { value }) => fetchImportableFields(value)}
                />
              </div>
              <div style={{ marginBottom: "5px", minWidth: "200px" }}>
                <Dropdown
                  placeholder="Filter by field type"
                  clearable
                  variant="outline"
                  options={templateFieldTypes
                    .filter((type) => type?.type === "general")
                    .map((type) => ({
                      key: type.key,
                      value: type.key,
                      text: type.name,
                    }))}
                  value={searchFieldType}
                  onChange={(_e, { value }) =>
                    setSearchFieldType(value as string)
                  }
                />
              </div>
            </div>

            {filteredFields.length === 0 ? (
              <p>No fields available to import.</p>
            ) : (
              <ConfigurableTable
                tableKey="challengeSectionFieldImport"
                selection={fieldSelection}
                loading={searchLoading}
                columns={[
                  {
                    key: "challengeName",
                    name: t("common:capitalise", { key: "generic.challenge" }),
                  },
                  { key: "sectionName", name: "Section" },
                  {
                    key: "fieldTitle",
                    name: "Field",
                    render: ({ item }) => (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {item.disabled && (
                          <em style={{ color: "red", marginBottom: 4 }}>
                            This field has already been added
                          </em>
                        )}
                        <p style={{ margin: 0 }}>
                          {getFieldDisplayName(item.field)}
                        </p>
                      </div>
                    ),
                  },
                ]}
                data={filteredFields}
              />
            )}
          </>
        )}
      </ModalContent>
      <ModalActions>
        <Button
          onClick={() => {
            fieldSelection.clear();
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            handleImport();
          }}
          disabled={selectedFields.length === 0}
          color="primary"
        >
          Import {selectedFields.length} Field
          {selectedFields.length !== 1 ? "s" : ""}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default FieldChooser;
